/*
 * Dice Tower System — configurator.
 *
 * Shows each real component (Tower panels, Vault, Tray) as its own small
 * native Shopify 3D preview (<product-model> / Shopify.ModelViewerUI — the
 * exact same mechanism already used on this store's individual product
 * pages) rather than assembling all parts into one combined 3D scene.
 * Recolors every visible model-viewer using the same base-color-factor
 * technique as product-3d-recolor.js, and adds the resulting build to cart
 * as 3 line items (Tower + Vault + Tray) sharing a _build_id property.
 */
(function () {
  'use strict';

  // TODO(store): sourced from the "Color" option's swatch metafield on
  // Dice Tower - Standard - Back Panel (2026-08-20). If colors are ever
  // added/renamed in Shopify admin, refresh this list to match — but keep
  // the F9.2 spacing below (see that comment) when you do.
  //
  // F9.2 fix: order only ever affected DISPLAY order (confirmed — the only
  // other read of this array is a random INDEX pick in randomizeAllColors,
  // order-independent) — reordered so the 3 visually similar muted
  // grayish/earthy tones (Silver, Gray, Bronze) are never adjacent in the
  // rendered swatch row, since a dense grid of small circles made them easy
  // to mis-tap for one another. No colors added or removed.
  var COLORS = [
    { name: 'Jade White', hex: '#FFFFFF' },
    { name: 'Gold', hex: '#E4BD68' },
    { name: 'Silver', hex: '#A6A9AA' },
    { name: 'Red', hex: '#C12E1F' },
    { name: 'Gray', hex: '#8E9089' },
    { name: 'Orange', hex: '#FF6A13' },
    { name: 'Bronze', hex: '#847D48' },
    { name: 'Yellow', hex: '#F4EE2A' },
    { name: 'Brown', hex: '#9D432C' },
    { name: 'Bambu Green', hex: '#00AE42' },
    { name: 'Pink', hex: '#F55A74' },
    { name: 'Cyan', hex: '#0086D6' },
    { name: 'Blue', hex: '#0A2989' },
    { name: 'Purple', hex: '#5E43B7' },
    { name: 'Black', hex: '#000000' }
  ];

  // Default starting color for every single-color part (per store owner's
  // reference screenshot — a light gray/silver reads far better under the
  // viewer's lighting than pure white, which blows out and hides surface
  // detail). "Set all" / per-part overrides still pick from the full list.
  var DEFAULT_COLOR = { name: 'Silver', hex: '#A6A9AA' };

  // Hex, Kumiko, Inverse Weave, Regal, Wicker, and Wyrmcron have real
  // products/models. Spiral is shown disabled with a "Coming Soon" tag,
  // matching templates/page.patterned-inserts.json.
  // Ordered so every available pattern comes before every "Coming Soon"
  // one (store owner request) rather than interleaving them.
  var PATTERNS = [
    { key: 'standard', label: 'Standard (No Insert)', available: true },
    { key: 'hex', label: 'Hex', available: true },
    { key: 'kumiko', label: 'Kumiko', available: true },
    { key: 'regal', label: 'Regal', available: true },
    { key: 'inverse-weave', label: 'Inverse Weave', available: true },
    { key: 'wicker', label: 'Wicker', available: true },
    { key: 'wyrmcron', label: 'Wyrmcron', available: true },
    { key: 'spiral', label: 'Spiral', available: true }
  ];

  // TODO(store): variant IDs from the 3 "Dice Tower System — ... Build"
  // products created in Shopify admin (2026-08-20). If those products are
  // ever recreated, update these.
  var VARIANTS = {
    tower: { standard: 46775108206789, patterned: 46775108239557 },
    vault: { standard: 46775108370629, patterned: 46775108403397 },
    tray: { standard: 46775108600005 },
    clamp: { standard: 46785182007493 }
  };

  // Total dropped again, $55/$80 -> $50/$65 (store owner's explicit new
  // pricing, "the same way as last time") — Tower/Vault scaled
  // proportionally per-tier from the CURRENT $55/$80 prices (Standard
  // ×10/11, Patterned ×13/16, matching the ratio of new total to current
  // total for each tier). Tray/Clamp are a single shared price across both
  // tiers (one variant each, not two), so they were scaled by the
  // Standard-tier ratio and Tower's Patterned price absorbs the small
  // residual needed to land Patterned exactly on $65.00 — same "absorb
  // into Tower" precedent as every prior round. These must match the real
  // Shopify variant prices on the "Dice Tower System — Tower/Vault/Tray/
  // Clamp Build" products, since that's what actually gets charged at
  // checkout, not this display calc.
  // Standard: 22.66 + 13.34 + 8.66 + 5.34 = 50.00
  // Patterned: 33.27 + 17.73 + 8.66 + 5.34 = 65.00
  var PRICES = {
    tower: { standard: 22.66, patterned: 33.27 },
    vault: { standard: 13.34, patterned: 17.73 },
    tray: { standard: 8.66, patterned: 8.66 },
    clamp: { standard: 5.34, patterned: 5.34 }
  };

  // Insert pieces (e.g. 'front-top-insert') are separate physical parts
  // that snap into a cutout in their matching patterned base panel — they
  // only exist for non-standard patterns, so the role list per silhouette
  // differs by pattern (used for both which cards to expect and the cart
  // color summary — see colorSummaryFor). The 7 "backer" panel roles
  // (back/bottom-baffle/top-baffle/front-bottom/front-top-patterned/left/
  // right) are the SAME physical products across every patterned tier —
  // the cutout that accepts an insert doesn't care which decorative insert
  // snaps into it — so every pattern's role list reuses those bare role
  // keys rather than suffixing them; only the truly pattern-specific insert
  // + crest pieces get a "-<pattern>" suffixed role key, so their state
  // (partColors/zoneColors), MULTICOLOR_ZONES entry, and GLB material
  // names don't collide with another pattern's own insert data.
  var SILHOUETTE_ROLES = {
    tower: {
      standard: ['back', 'bottom-baffle', 'front-bottom', 'front-top', 'left', 'right', 'top-baffle'],
      hex: [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert',
        'front-top-patterned', 'front-top-insert',
        'left', 'left-insert',
        'right', 'right-insert',
        'crest-emblem'
      ],
      kumiko: [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert-kumiko',
        'front-top-patterned', 'front-top-insert-kumiko',
        'left', 'left-insert-kumiko',
        'right', 'right-insert-kumiko',
        'crest-emblem-kumiko'
      ],
      'inverse-weave': [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert-inverse-weave',
        'front-top-patterned', 'front-top-insert-inverse-weave',
        'left', 'left-insert-inverse-weave',
        'right', 'right-insert-inverse-weave',
        'crest-emblem-inverse-weave'
      ],
      regal: [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert-regal',
        'front-top-patterned', 'front-top-insert-regal',
        'left', 'left-insert-regal',
        'right', 'right-insert-regal',
        'crest-emblem-regal'
      ],
      wicker: [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert-wicker',
        'front-top-patterned', 'front-top-insert-wicker',
        'left', 'left-insert-wicker',
        'right', 'right-insert-wicker',
        'crest-emblem-wicker'
      ],
      wyrmcron: [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert-wyrmcron',
        'front-top-patterned', 'front-top-insert-wyrmcron',
        'left', 'left-insert-wyrmcron',
        'right', 'right-insert-wyrmcron',
        'crest-emblem-wyrmcron'
      ],
      spiral: [
        'back', 'bottom-baffle', 'top-baffle',
        'front-bottom', 'front-bottom-insert-spiral',
        'front-top-patterned', 'front-top-insert-spiral',
        'left', 'left-insert-spiral',
        'right', 'right-insert-spiral',
        'crest-emblem-spiral'
      ]
    },
    vault: {
      standard: ['base', 'lid'],
      hex: ['base', 'lid', 'lid-insert'],
      kumiko: ['base', 'lid', 'lid-insert-kumiko'],
      'inverse-weave': ['base', 'lid', 'lid-insert-inverse-weave'],
      regal: ['base', 'lid', 'lid-insert-regal'],
      wicker: ['base', 'lid', 'lid-insert-wicker'],
      wyrmcron: ['base', 'lid', 'lid-insert-wyrmcron'],
      spiral: ['base', 'lid', 'lid-insert-spiral']
    },
    tray: {
      standard: ['tray-body', 'tray-feet'],
      hex: ['tray-body', 'tray-feet'],
      kumiko: ['tray-body', 'tray-feet'],
      'inverse-weave': ['tray-body', 'tray-feet'],
      regal: ['tray-body', 'tray-feet'],
      wicker: ['tray-body', 'tray-feet'],
      wyrmcron: ['tray-body', 'tray-feet'],
      spiral: ['tray-body', 'tray-feet']
    },
    // Its own entry (not folded into `tray` above) so it gets its own cart
    // line item, matching that it's now separately priced rather than a
    // free customization of the Tray line.
    clamp: {
      standard: ['tray-clamp'],
      hex: ['tray-clamp'],
      kumiko: ['tray-clamp'],
      'inverse-weave': ['tray-clamp'],
      regal: ['tray-clamp'],
      wicker: ['tray-clamp'],
      wyrmcron: ['tray-clamp'],
      spiral: ['tray-clamp']
    }
  };

  // Resolves a pattern key to the SILHOUETTE_ROLES group it should use —
  // every pattern with its own role lists above resolves to itself,
  // anything else (an unrecognized or not-yet-built pattern key) falls
  // back to 'standard' rather than silently reusing Hex's cards, which is
  // what the old `pattern === 'hex' ? 'hex' : 'standard'` shortcut did.
  function patternGroupFor(pattern) {
    return SILHOUETTE_ROLES.tower.hasOwnProperty(pattern) ? pattern : 'standard';
  }

  // The active pattern's own 'left-insert'-family role key — Hex's is the
  // bare 'left-insert', every other pattern suffixes it (e.g.
  // 'left-insert-kumiko'). Used wherever code needs "whichever side-insert
  // pair is currently on screen" instead of Hex's hardcoded role name.
  function activeLeftInsertRole(pattern) {
    var group = patternGroupFor(pattern);
    return group === 'hex' ? 'left-insert' : 'left-insert-' + group;
  }

  var ROLE_LABELS = {
    back: 'Back Panel', 'bottom-baffle': 'Bottom Inner Rolling Surface', 'front-bottom': 'Front Bottom Panel',
    'front-bottom-insert': 'Front Bottom Insert (Hex)',
    'front-bottom-insert-kumiko': 'Front Bottom Insert (Kumiko)',
    'front-bottom-insert-inverse-weave': 'Front Bottom Insert (Inverse Weave)',
    'front-bottom-insert-regal': 'Front Bottom Insert (Regal)',
    'front-bottom-insert-wicker': 'Front Bottom Insert (Wicker)',
    'front-bottom-insert-wyrmcron': 'Front Bottom Insert (Wyrmcron)',
    'front-bottom-insert-spiral': 'Front Bottom Insert (Spiral)',
    'front-top': 'Front Top Panel', 'front-top-patterned': 'Front Top Panel (Patterned)', 'front-top-insert': 'Front Top Insert (Hex)',
    'front-top-insert-kumiko': 'Front Top Insert (Kumiko)',
    'front-top-insert-inverse-weave': 'Front Top Insert (Inverse Weave)',
    'front-top-insert-regal': 'Front Top Insert (Regal)',
    'front-top-insert-wicker': 'Front Top Insert (Wicker)',
    'front-top-insert-wyrmcron': 'Front Top Insert (Wyrmcron)',
    'front-top-insert-spiral': 'Front Top Insert (Spiral)',
    left: 'Left Side Panel', 'left-insert': 'Left Side Insert (Hex)',
    'left-insert-kumiko': 'Left Side Insert (Kumiko)',
    'left-insert-inverse-weave': 'Left Side Insert (Inverse Weave)',
    'left-insert-regal': 'Left Side Insert (Regal)',
    'left-insert-wicker': 'Left Side Insert (Wicker)',
    'left-insert-wyrmcron': 'Left Side Insert (Wyrmcron)',
    'left-insert-spiral': 'Left Side Insert (Spiral)',
    right: 'Right Side Panel', 'right-insert': 'Right Side Insert (Hex)',
    'right-insert-kumiko': 'Right Side Insert (Kumiko)',
    'right-insert-inverse-weave': 'Right Side Insert (Inverse Weave)',
    'right-insert-regal': 'Right Side Insert (Regal)',
    'right-insert-wicker': 'Right Side Insert (Wicker)',
    'right-insert-wyrmcron': 'Right Side Insert (Wyrmcron)',
    'right-insert-spiral': 'Right Side Insert (Spiral)',
    'top-baffle': 'Top Inner Rolling Surface', base: 'Vault Base', lid: 'Vault Lid', 'lid-insert': 'Vault Lid Insert (Hex)',
    'lid-insert-kumiko': 'Vault Lid Insert (Kumiko)',
    'lid-insert-inverse-weave': 'Vault Lid Insert (Inverse Weave)',
    'lid-insert-regal': 'Vault Lid Insert (Regal)',
    'lid-insert-wicker': 'Vault Lid Insert (Wicker)',
    'lid-insert-wyrmcron': 'Vault Lid Insert (Wyrmcron)',
    'lid-insert-spiral': 'Vault Lid Insert (Spiral)',
    'tray-body': 'Tray Body', 'tray-feet': 'Tray Feet', 'tray-clamp': 'Tray Clamp',
    'crest-emblem': 'Crest Emblem',
    'crest-emblem-kumiko': 'Crest Emblem',
    'crest-emblem-inverse-weave': 'Crest Emblem',
    'crest-emblem-regal': 'Crest Emblem',
    'crest-emblem-wicker': 'Crest Emblem',
    'crest-emblem-wyrmcron': 'Crest Emblem',
    'crest-emblem-spiral': 'Crest Emblem'
  };

  // Parts whose real production 3D file only has one flat material even
  // though the physical product is sold as a multi-color AMS print — these
  // src assets are custom reconstructed multi-material GLBs (see
  // dice-tower-multicolor-part-preview.liquid) built from the store owner's
  // own Bambu Studio .3mf source files, with these 3 materials in this
  // exact index order. Default colors are the exact hex values from those
  // reference builds, so the preview matches the real thing before any
  // customer override. The source files' own material names were generic
  // ("Accent 1"/"Accent 2"/"Core"), so labels below describe what each
  // actually is on the geometry rather than reusing those generic names.
  var MULTICOLOR_ZONES = {
    // True extracted colors were Charcoal/Gold/Red (the source owner's own
    // AMS filament choice) — switched to the same Silver default used
    // everywhere else, with the "Top Panel" zone kept Black so the
    // WYRMWOOD text stays legible against it (same logic as front-top's
    // Silver body / Black logo).
    'tray-body': [
      { label: 'Top and Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Trim Accent', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inner Band', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'tray-clamp': [
      { label: 'Top and Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Trim Accent', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top Panel and Middle Body', materialIndex: 2, hex: '#000000', name: 'Black' }
    ],
    // Source file only defines 2 distinct extruder/filament assignments
    // (confirmed via model_settings.config: extruder 2 = #6F5034 Brown,
    // extruder 4 = #9D2235 Maroon) — not forcing a 3rd zone. Those were
    // just whatever filament the source owner happened to use in Bambu
    // Studio, not a canonical default, so the STARTING colors below are
    // set to the requested Silver body / Black logo look instead.
    'front-top': [
      { label: 'Body', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Logo', materialIndex: 1, hex: '#000000', name: 'Black' }
    ],

    // Hex pattern insert pieces — each is a real 4-material AMS print (3
    // hex-grid tiers plus the solid base fill everything else sits on),
    // reconstructed the same way as tray-body/tray-clamp above from the
    // store owner's own Bambu Studio .3mf source files. Zone labels and
    // face-count order below are read directly from those source files'
    // part names, not invented. Left/Right side inserts share one source
    // file (the geometry is mirrored, not re-authored per side), so their
    // zone lists are identical.
    'front-bottom-insert': [
      { label: 'Three Top Hexagons', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Three Bottom Hexagons', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert': [
      { label: 'Top and Bottom Hexagons', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Two Top Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Two Bottom Hexagons', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert': [
      { label: 'Bottom Outer Rim Hexagons', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top Outer Rim Hexagons', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert': [
      { label: 'Bottom Outer Rim Hexagons', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top Outer Rim Hexagons', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert': [
      { label: 'Accent Trim', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Outer Hexagons', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],

    // Kumiko/Inverse Weave/Regal insert pieces — same reconstruction
    // process as the Hex zones above, from THIS store's own Bambu Studio
    // .3mf source files for each pattern (zone counts are NOT uniform
    // across patterns — Kumiko's real print is a 3-material AMS build,
    // Inverse Weave/Regal are 4-material like Hex — verified per-part by
    // counting each .3mf's own <component> zones, not assumed). Zone
    // labels/order below are read directly from each source file's own
    // part names (model_settings.config), same as Hex's. Default colors
    // use Hex's own Silver convention (the store owner's real AMS filament
        // choices weren't a canonical default for Hex either — see the
    // comment above applyFn — so there's no reason to special-case these).
    'front-bottom-insert-kumiko': [
      { label: 'Background', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-kumiko': [
      { label: 'Background', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert-kumiko': [
      { label: 'Background', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert-kumiko': [
      { label: 'Background', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-kumiko': [
      { label: 'Background', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Hexagons', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],

    'front-bottom-insert-inverse-weave': [
      { label: 'Inside Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-inverse-weave': [
      { label: 'Inside Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert-inverse-weave': [
      { label: 'Inside Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert-inverse-weave': [
      { label: 'Inside Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-inverse-weave': [
      { label: 'Inside Bottom', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Middle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Inside Top', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],

    'front-bottom-insert-regal': [
      { label: 'Square', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-regal': [
      { label: 'Square', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert-regal': [
      { label: 'Square', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert-regal': [
      { label: 'Square', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-regal': [
      { label: 'Square', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Upside Down Triangle', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Rightside Up Triangle', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],

    // Wicker insert pieces — same reconstruction process as every pattern
    // above, from this store's own Bambu Studio .3mf source files. Wicker
    // is genuinely a 2-material AMS build (verified per-part by counting
    // its .3mf's own <component> zones) — a simple decorative relief zone
    // plus its outer trim/border zone, confirmed via its source files' own
    // part names (model_settings.config).
    'front-bottom-insert-wicker': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-wicker': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert-wicker': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert-wicker': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-wicker': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' }
    ],

    // Wyrmcron insert pieces — genuinely a 3-material AMS build, but the
    // third zone was never a separate object in the source .3mf files the
    // way Border/Background are — it was painted directly onto the
    // Background mesh via Bambu Studio's per-triangle multi-material paint
    // tool (a different filament color, assigned to make the zone
    // identifiable), extracted into a real third part named "Pattern" per
    // the store owner's own naming request (see
    // split_wyrmcron_pattern_zone.py in the pipeline directory for the
    // extraction, and each rebuilt zone-part's own visual verification
    // before this was wired up). Defaults to Silver, matching every other
    // zone/part on the configurator — the store owner had this default to
    // Black originally (matching the filament color it was painted with)
    // but later asked for Silver everywhere except the Standard front-top
    // Logo zone and the personal tray, which are the only two exceptions.
    'front-bottom-insert-wyrmcron': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-wyrmcron': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert-wyrmcron': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert-wyrmcron': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-wyrmcron': [
      { label: 'Border', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Background', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' }
    ],

    // Spiral — genuinely 4 real 3MF objects per zone (unlike Wyrmcron, no
    // paint-attribute extraction needed), all 4 customer-configurable per
    // the store owner's explicit request after the shop-page pilot ("i want
    // those colors avaiable for color selection also, so four colors,
    // bottom, middle, top and pattern" — see the shop-page ZONE_DEFS entry
    // in dice-tower-shop.js for the same decision applied there first).
    // materialIndex 0/1 (Bottom/Middle of Spiral) are correct and stable.
    // materialIndex 2/3 for Top of Spiral/Pattern were SWAPPED from an
    // earlier version of this file, which had them the other way around
    // based on build_pattern.py's SPIRAL_ZONE_LABELS naming — that turned
    // out not to match the actual GLB's material order once checked
    // against the live 3D preview (confirmed by the store owner directly:
    // the "Top of Spiral" swatch was visibly recoloring the Pattern
    // geometry and vice versa). materialIndex: 2 is Top of Spiral and
    // materialIndex: 3 is Pattern — display order (this array's own
    // top-to-bottom order, Pattern listed last) is unrelated and unchanged.
    // This only works because applyZoneColorsToViewer/buildMaterialColorMapFor
    // key by materialIndex, not array position — see those functions' own
    // comments if touching this again. All 4 zones default to Silver,
    // matching every other zone/part on the configurator (Pattern
    // originally defaulted to Yellow, its baked filament color from the
    // pilot build, but the store owner later asked for Silver everywhere
    // except the Standard front-top Logo zone and the personal tray).
    'front-bottom-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'front-top-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'left-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'right-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ],
    'lid-insert-spiral': [
      { label: 'Bottom of Spiral', materialIndex: 0, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Middle of Spiral', materialIndex: 1, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Top of Spiral', materialIndex: 2, hex: '#A6A9AA', name: 'Silver' },
      { label: 'Pattern', materialIndex: 3, hex: '#A6A9AA', name: 'Silver' }
    ]
  };

  // Used by the "Match Left & Right Insert Colors" lock — see bindEvents.
  var SIDE_INSERT_PAIR = {
    'left-insert': 'right-insert', 'right-insert': 'left-insert',
    'left-insert-kumiko': 'right-insert-kumiko', 'right-insert-kumiko': 'left-insert-kumiko',
    'left-insert-inverse-weave': 'right-insert-inverse-weave', 'right-insert-inverse-weave': 'left-insert-inverse-weave',
    'left-insert-regal': 'right-insert-regal', 'right-insert-regal': 'left-insert-regal',
    'left-insert-wicker': 'right-insert-wicker', 'right-insert-wicker': 'left-insert-wicker',
    'left-insert-wyrmcron': 'right-insert-wyrmcron', 'right-insert-wyrmcron': 'left-insert-wyrmcron',
    'left-insert-spiral': 'right-insert-spiral', 'right-insert-spiral': 'left-insert-spiral'
  };

  // The crest emblem is the SAME physical part regardless of pattern
  // (byte-identical geometry, confirmed face_count 27256 everywhere — see
  // build_pattern.py's docstring) — it's only pattern-suffixed in state at
  // all because it's sold as a separate per-pattern product/line item at
  // checkout, not because it's a different decorative piece the way the
  // insert roles are. Customers expect its color choice to carry over when
  // switching patterns, same as the shared backer-panel roles (back,
  // bottom-baffle, etc) already do for free by reusing one bare role key —
  // crest can't reuse a bare key (its role key has to stay pattern-specific
  // for the cart/product line-item to resolve to the right SKU), so instead
  // every crest-emblem-* role's color state is kept in sync explicitly
  // whenever any one of them changes — see the per-part swatch handler in
  // bindEvents. refreshVisibleParts() already re-applies state.partColors
  // to whichever card becomes visible on a pattern switch, so keeping this
  // list in sync is the only piece actually needed for the color to
  // "stick" across patterns.
  var CREST_EMBLEM_ROLES = [
    'crest-emblem', 'crest-emblem-kumiko', 'crest-emblem-inverse-weave',
    'crest-emblem-regal', 'crest-emblem-wicker', 'crest-emblem-wyrmcron',
    'crest-emblem-spiral'
  ];

  function DiceTowerConfigurator(root) {
    this.root = root;
    this.state = {
      silhouette: 'tower',
      pattern: 'standard',
      // Per-part color, keyed by role — every part starts on the default
      // color; each part-card also gets its own small swatch row so any
      // part can be overridden individually (matches how these are
      // actually sold — multicolor AMS prints).
      partColors: {},
      // Per-zone colors for the few parts with real multi-material
      // geometry (see MULTICOLOR_ZONES) — keyed by role, value is an array
      // parallel to that role's zone list.
      zoneColors: {},
      // When true, picking a zone color on Left or Right Side Insert (Hex)
      // applies it to both — most builds want a symmetric tower, so this
      // defaults on; unchecking lets the two sides diverge again.
      linkSideInserts: true
    };
    // F5.3: single-level undo for the last per-part/zone swatch click —
    // not part of `state` since it's transient UI bookkeeping, not app
    // data. See the data-undo-last-color handler in bindEvents.
    this.lastColorChange = null;
    var allRoles = [].concat(
      SILHOUETTE_ROLES.tower.standard, SILHOUETTE_ROLES.tower.hex,
      SILHOUETTE_ROLES.tower.kumiko, SILHOUETTE_ROLES.tower['inverse-weave'], SILHOUETTE_ROLES.tower.regal,
      SILHOUETTE_ROLES.tower.wicker, SILHOUETTE_ROLES.tower.wyrmcron, SILHOUETTE_ROLES.tower.spiral,
      SILHOUETTE_ROLES.vault.standard, SILHOUETTE_ROLES.vault.hex,
      SILHOUETTE_ROLES.vault.kumiko, SILHOUETTE_ROLES.vault['inverse-weave'], SILHOUETTE_ROLES.vault.regal,
      SILHOUETTE_ROLES.vault.wicker, SILHOUETTE_ROLES.vault.wyrmcron, SILHOUETTE_ROLES.vault.spiral,
      SILHOUETTE_ROLES.tray.standard
    );
    allRoles.forEach(function (role) {
      if (MULTICOLOR_ZONES[role]) return; // handled via zoneColors below instead
      if (this.state.partColors[role]) return; // roles repeat across standard/hex lists
      this.state.partColors[role] = { hex: DEFAULT_COLOR.hex, name: DEFAULT_COLOR.name };
    }, this);
    Object.keys(MULTICOLOR_ZONES).forEach(function (role) {
      this.state.zoneColors[role] = MULTICOLOR_ZONES[role].map(function (zone) {
        return { hex: zone.hex, name: zone.name };
      });
    }, this);

    // The multicolor part cards use a plain <model-viewer> rather than
    // <product-model>, so nothing else guarantees the custom element is
    // registered before refreshVisibleParts() below tries to use one —
    // normally a sibling product-model card triggers this as a side effect
    // of loadContent(), but that's incidental; trigger it directly instead
    // of depending on card visit order.
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: function () {} }]);
    }

    this.renderOptionControls();
    this.renderPerPartSwatches();
    this.bindEvents();
    this.updatePrice();
    this.refreshVisibleParts();
    this.updateSelectionsSummary();
  }

  DiceTowerConfigurator.prototype.renderOptionControls = function () {
    var patternWrap = this.root.querySelector('[data-pattern-options]');
    PATTERNS.forEach(function (pattern) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dice-configurator__option-btn' + (pattern.available ? '' : ' dice-configurator__option-btn--disabled');
      btn.dataset.pattern = pattern.key;
      btn.disabled = !pattern.available;
      btn.textContent = pattern.label + (pattern.available ? '' : ' (Coming Soon)');
      if (pattern.key === 'standard') btn.classList.add('is-selected');
      patternWrap.appendChild(btn);
    });

    // This swatch list sets every part's color at once (a bulk "set all" —
    // per-part overrides live on each card below the model instead, since
    // there's no single "current" color once parts can differ).
    var colorWrap = this.root.querySelector('[data-color-options]');
    COLORS.forEach(function (color) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dice-configurator__swatch';
      btn.dataset.colorHex = color.hex;
      btn.dataset.colorName = color.name;
      btn.setAttribute('aria-label', 'Set all parts to ' + color.name);

      var circle = document.createElement('span');
      circle.className = 'dice-configurator__swatch-circle';
      circle.style.setProperty('--swatch-color', color.hex);
      circle.setAttribute('aria-hidden', 'true');

      var label = document.createElement('span');
      label.className = 'dice-configurator__swatch-label';
      label.textContent = color.name;

      btn.appendChild(circle);
      btn.appendChild(label);
      colorWrap.appendChild(btn);
    });
  };

  // Adds a small swatch row under each part card's model, letting that one
  // part's color be overridden independently of the others. Multicolor
  // parts (see MULTICOLOR_ZONES) get one such row PER ZONE instead of one
  // for the whole part.
  DiceTowerConfigurator.prototype.renderPerPartSwatches = function () {
    var self = this;

    function buildSwatchRow(selectedHex, onEachBtn) {
      var row = document.createElement('div');
      row.className = 'dice-configurator__part-swatch-row';
      COLORS.forEach(function (color) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'dice-configurator__part-swatch' + (color.hex === selectedHex ? ' is-selected' : '');
        btn.dataset.colorHexValue = color.hex;
        btn.dataset.colorNameValue = color.name;
        btn.setAttribute('aria-label', color.name);
        onEachBtn(btn);

        var circle = document.createElement('span');
        circle.className = 'dice-configurator__part-swatch-circle';
        circle.style.setProperty('--swatch-color', color.hex);
        circle.setAttribute('aria-hidden', 'true');

        var label = document.createElement('span');
        label.className = 'dice-configurator__part-swatch-label';
        label.textContent = color.name;

        btn.appendChild(circle);
        btn.appendChild(label);
        row.appendChild(btn);
      });
      return row;
    }

    this.root.querySelectorAll('.dice-configurator__part-card').forEach(function (card) {
      var role = card.dataset.partRole;
      var zones = MULTICOLOR_ZONES[role];

      if (zones) {
        zones.forEach(function (zone, zoneIndex) {
          var heading = document.createElement('p');
          heading.className = 'dice-configurator__zone-row-label';
          heading.textContent = zone.label;
          var wrap = document.createElement('div');
          wrap.className = 'dice-configurator__zone-row';
          wrap.appendChild(heading);
          var row = buildSwatchRow(self.state.zoneColors[role][zoneIndex].hex, function (btn) {
            btn.dataset.zoneColorHex = btn.dataset.colorHexValue;
            btn.dataset.zoneColorName = btn.dataset.colorNameValue;
            btn.dataset.zoneIndex = zoneIndex;
          });
          wrap.appendChild(row);
          card.appendChild(wrap);
        });
        return;
      }

      var partRow = buildSwatchRow(self.state.partColors[role].hex, function (btn) {
        btn.dataset.partColorHex = btn.dataset.colorHexValue;
        btn.dataset.partColorName = btn.dataset.colorNameValue;
      });
      card.appendChild(partRow);
    });
  };

  // F8.4 fix: swatch clicks only ever produced a visual change (a
  // recolored 3D model) with no text output anywhere — a screen-reader
  // user had no way to know a color was actually applied. Writes into the
  // per-card visually-hidden role="status" element added in
  // dice-tower-part-preview.liquid/dice-tower-multicolor-part-preview.liquid.
  function announcePartColor(card, text) {
    var statusEl = card && card.querySelector('[data-part-color-status]');
    if (statusEl) statusEl.textContent = text;
  }

  DiceTowerConfigurator.prototype.bindEvents = function () {
    var self = this;
    // Randomize button's "click again to confirm" state — a plain closure
    // var rather than something on this.state, since it's transient UI
    // state (which of two button labels is showing), not app data that
    // needs to survive a pattern/silhouette switch or an add-to-cart.
    var randomizeConfirmTimeout = null;
    // Same pattern for the "Set a Single Color" swatches — since it's a
    // grid of many buttons rather than one, this also tracks WHICH swatch
    // is currently armed, so a second click on a DIFFERENT swatch disarms
    // the first instead of applying its color.
    var colorConfirmTimeout = null;
    var armedColorBtn = null;
    function disarmColorSwatch(btn) {
      btn.classList.remove('is-confirming');
      var label = btn.querySelector('.dice-configurator__swatch-label');
      if (label) label.textContent = btn.dataset.colorName;
    }

    this.root.addEventListener('click', function (e) {
      var tabBtn = e.target.closest('[data-silhouette-tab]');
      if (tabBtn) {
        self.root.querySelectorAll('[data-silhouette-tab]').forEach(function (b) { b.classList.remove('is-selected'); });
        tabBtn.classList.add('is-selected');
        self.state.silhouette = tabBtn.dataset.silhouetteTab;
        self.refreshVisibleParts();
        return;
      }

      var patternBtn = e.target.closest('[data-pattern]');
      if (patternBtn && !patternBtn.disabled) {
        self.root.querySelectorAll('[data-pattern]').forEach(function (b) { b.classList.remove('is-selected'); });
        patternBtn.classList.add('is-selected');
        self.state.pattern = patternBtn.dataset.pattern;
        self.updatePrice();
        self.refreshVisibleParts();
        // colorSummaryFor's role list depends on the active pattern, so
        // the F3.2 selections summary needs a refresh here too, not just
        // on the color-mutating actions refreshCompletedTowerColors covers.
        self.updateSelectionsSummary();
        return;
      }

      // "Set a Single Color" swatch (the main Color section) — applies to
      // every part, including every zone of multicolor parts, so this
      // actually means everything matches. Click-again-to-confirm, same
      // reasoning as the Randomize button below: this is a much bigger,
      // harder-to-undo change than a per-part swatch (which only touches
      // the one part it's on), so it shouldn't fire off a single click,
      // especially on a touch device where a mis-tap on a dense swatch
      // grid is easy. Only one swatch can be armed at a time — clicking a
      // second swatch while another is still armed disarms the first and
      // arms the new one instead of applying either, since silently
      // applying the FIRST swatch's color off a click meant for a
      // different one would be a worse mistake than requiring one more
      // click.
      var colorBtn = e.target.closest('[data-color-hex]');
      if (colorBtn) {
        if (colorBtn.classList.contains('is-confirming')) {
          clearTimeout(colorConfirmTimeout);
          disarmColorSwatch(colorBtn);
          armedColorBtn = null;

          var hex = colorBtn.dataset.colorHex;
          var name = colorBtn.dataset.colorName;
          Object.keys(self.state.partColors).forEach(function (role) {
            self.state.partColors[role] = { hex: hex, name: name };
          });
          Object.keys(self.state.zoneColors).forEach(function (role) {
            self.state.zoneColors[role] = self.state.zoneColors[role].map(function () {
              return { hex: hex, name: name };
            });
          });
          self.root.querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
            b.classList.toggle('is-selected', b.dataset.colorHexValue === hex);
          });
          self.applyColorToVisibleParts();
          self.refreshCompletedTowerColors();
          // F5.3: a bulk repaint makes "undo the last single part/zone
          // change" no longer a meaningful scope — this action already
          // has its own click-twice-confirm safety net instead.
          self.lastColorChange = null;
          self.updateUndoButtonVisibility();
        } else {
          if (armedColorBtn && armedColorBtn !== colorBtn) {
            clearTimeout(colorConfirmTimeout);
            disarmColorSwatch(armedColorBtn);
          }
          colorBtn.classList.add('is-confirming');
          var confirmLabel = colorBtn.querySelector('.dice-configurator__swatch-label');
          if (confirmLabel) confirmLabel.textContent = 'Confirm?';
          armedColorBtn = colorBtn;
          colorConfirmTimeout = setTimeout(function () {
            disarmColorSwatch(colorBtn);
            armedColorBtn = null;
          }, 4000);
        }
        return;
      }

      // Randomize All Colors — click-again-to-confirm instead of a native
      // confirm() (which blocks the JS thread and looks jarring next to
      // the rest of this UI). First click swaps the button into a
      // "confirming" state for a few seconds; a second click while still
      // in that state is what actually runs the randomization. Clicking
      // it again later (after the window lapses) just re-arms it rather
      // than firing immediately — an accidental double-click months apart
      // shouldn't nuke a customer's careful color choices.
      var randomizeBtn = e.target.closest('[data-randomize-colors]');
      if (randomizeBtn) {
        if (randomizeBtn.classList.contains('is-confirming')) {
          clearTimeout(randomizeConfirmTimeout);
          randomizeBtn.classList.remove('is-confirming');
          randomizeBtn.textContent = 'Randomize All Colors';
          self.randomizeAllColors();
        } else {
          randomizeBtn.classList.add('is-confirming');
          // F3.1 fix: randomizeAllColors() deliberately spans every
          // pattern's saved colors, not just the one on screen (see that
          // method's own doc comment) — the confirm step didn't say so, so
          // a customer who'd already colored, say, Hex and switched to
          // check Standard could randomize what they thought was only the
          // visible Standard parts and silently lose their Hex choices too.
          randomizeBtn.textContent = 'Click again to confirm (affects every pattern, not just this one)';
          randomizeConfirmTimeout = setTimeout(function () {
            randomizeBtn.classList.remove('is-confirming');
            randomizeBtn.textContent = 'Randomize All Colors';
          }, 4000);
        }
        return;
      }

      // Per-part swatch — overrides just the one part it's on.
      var partColorBtn = e.target.closest('[data-part-color-hex]');
      if (partColorBtn) {
        var card = partColorBtn.closest('.dice-configurator__part-card');
        var role = card.dataset.partRole;
        var pickedColor = { hex: partColorBtn.dataset.partColorHex, name: partColorBtn.dataset.partColorName };

        // F5.3: capture the previous color(s) BEFORE overwriting state,
        // for the single-level undo below — includes every crest-emblem
        // role this click also syncs (see CREST_EMBLEM_ROLES' own
        // comment), so undo reverts all of them, not just the one
        // actually clicked.
        var affectedPartRoles = CREST_EMBLEM_ROLES.indexOf(role) !== -1 ? CREST_EMBLEM_ROLES : [role];
        var partChanges = affectedPartRoles.map(function (r) {
          return { role: r, previousColor: self.state.partColors[r] };
        });

        self.state.partColors[role] = pickedColor;
        card.querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
          b.classList.toggle('is-selected', b === partColorBtn);
        });
        var mv = card.querySelector('model-viewer');
        if (mv) self.applyColorToViewer(mv, role);
        announcePartColor(card, (ROLE_LABELS[role] || 'Part') + ' set to ' + pickedColor.name);

        // Keep every pattern's crest emblem color in sync — see
        // CREST_EMBLEM_ROLES' own comment for why (same physical part,
        // just a different SKU per pattern at checkout).
        if (CREST_EMBLEM_ROLES.indexOf(role) !== -1) {
          CREST_EMBLEM_ROLES.forEach(function (crestRole) {
            self.state.partColors[crestRole] = { hex: pickedColor.hex, name: pickedColor.name };
          });
        }

        self.lastColorChange = { kind: 'part', partChanges: partChanges };
        self.updateUndoButtonVisibility();

        self.refreshCompletedTowerColors();
        return;
      }

      // Zone swatch (multicolor parts) — overrides just that one material.
      var zoneColorBtn = e.target.closest('[data-zone-color-hex]');
      if (zoneColorBtn) {
        var zoneCard = zoneColorBtn.closest('.dice-configurator__part-card');
        var zoneRole = zoneCard.dataset.partRole;
        var zoneIndex = parseInt(zoneColorBtn.dataset.zoneIndex, 10);
        var newZoneColor = { hex: zoneColorBtn.dataset.zoneColorHex, name: zoneColorBtn.dataset.zoneColorName };

        // F5.3: capture previous color(s) before overwriting, same
        // reasoning as the per-part swatch handler above.
        var zoneChanges = [{ role: zoneRole, zoneIndex: zoneIndex, previousColor: self.state.zoneColors[zoneRole][zoneIndex] }];

        self.state.zoneColors[zoneRole][zoneIndex] = newZoneColor;
        zoneColorBtn.closest('.dice-configurator__zone-row').querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
          b.classList.toggle('is-selected', b === zoneColorBtn);
        });
        var zoneMv = zoneCard.querySelector('model-viewer');
        if (zoneMv) self.applyZoneColorsToViewer(zoneMv, zoneRole);
        var zoneLabel = (MULTICOLOR_ZONES[zoneRole] && MULTICOLOR_ZONES[zoneRole][zoneIndex] && MULTICOLOR_ZONES[zoneRole][zoneIndex].label) || 'Zone';
        announcePartColor(zoneCard, zoneLabel + ' set to ' + newZoneColor.name);

        // Mirror onto the other side when Left/Right Insert colors are locked.
        var mirrorRole = SIDE_INSERT_PAIR[zoneRole];
        if (mirrorRole && self.state.linkSideInserts) {
          zoneChanges.push({ role: mirrorRole, zoneIndex: zoneIndex, previousColor: self.state.zoneColors[mirrorRole][zoneIndex] });
          self.state.zoneColors[mirrorRole][zoneIndex] = { hex: newZoneColor.hex, name: newZoneColor.name };
          self.setZoneSwatchUI(mirrorRole, zoneIndex, newZoneColor.hex);
          var mirrorCard = self.root.querySelector('.dice-configurator__part-card[data-part-role="' + mirrorRole + '"]');
          var mirrorMv = mirrorCard && mirrorCard.querySelector('model-viewer');
          if (mirrorMv) self.applyZoneColorsToViewer(mirrorMv, mirrorRole);
          if (mirrorCard) announcePartColor(mirrorCard, zoneLabel + ' set to ' + newZoneColor.name + ' (matched from linked side)');
        }

        self.lastColorChange = { kind: 'zone', zoneChanges: zoneChanges };
        self.updateUndoButtonVisibility();

        self.refreshCompletedTowerColors();
        return;
      }

      // F5.3: single-level undo for the last per-part/zone swatch click.
      var undoColorBtn = e.target.closest('[data-undo-last-color]');
      if (undoColorBtn) {
        self.undoLastColorChange();
        return;
      }

      // Lock/unlock toggle for Left & Right Side Insert colors — turning it
      // on immediately snaps Right to match Left's current colors so the
      // two sides don't stay mismatched until the next swatch click.
      var linkToggle = e.target.closest('[data-link-side-inserts]');
      if (linkToggle) {
        self.state.linkSideInserts = linkToggle.checked;
        if (self.state.linkSideInserts) {
          // Hardcoding Hex's own 'left-insert'/'right-insert' role keys
          // here would silently no-op for every other pattern — look up
          // whichever pair is actually active instead.
          var activeLeftRole = activeLeftInsertRole(self.state.pattern);
          var activeRightRole = SIDE_INSERT_PAIR[activeLeftRole];
          self.state.zoneColors[activeRightRole] = self.state.zoneColors[activeLeftRole].map(function (c) {
            return { hex: c.hex, name: c.name };
          });
          self.state.zoneColors[activeRightRole].forEach(function (c, i) { self.setZoneSwatchUI(activeRightRole, i, c.hex); });
          var rightCard = self.root.querySelector('.dice-configurator__part-card[data-part-role="' + activeRightRole + '"]');
          var rightMv = rightCard && rightCard.querySelector('model-viewer');
          if (rightMv) self.applyZoneColorsToViewer(rightMv, activeRightRole);
          self.refreshCompletedTowerColors();
        }
        return;
      }

      var sheetToggle = e.target.closest('[data-sheet-toggle]');
      if (sheetToggle) {
        self.root.classList.toggle('dice-configurator--sheet-open');
        return;
      }

      // Backdrop tap or the panel's own close button — always closes rather
      // than toggling, so it can't accidentally reopen the sheet.
      var sheetClose = e.target.closest('[data-sheet-close]');
      if (sheetClose) {
        self.root.classList.remove('dice-configurator--sheet-open');
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        self.root.classList.remove('dice-configurator--sheet-open');
      }
    });

    var addToCartBtn = this.root.querySelector('[data-add-to-cart]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', function () { self.addToCart(); });
    }
  };

  // Shows only the cards matching the active tab + pattern, loads any of
  // them that haven't rendered their <model-viewer> yet (via the theme's
  // existing DeferredMedia.loadContent — same lazy-load path a normal
  // product page's 3D media uses), and recolors once loaded.
  DiceTowerConfigurator.prototype.refreshVisibleParts = function () {
    var self = this;
    var patternGroup = patternGroupFor(this.state.pattern);

    this.root.querySelectorAll('[data-silhouette-note]').forEach(function (note) {
      note.hidden = note.dataset.silhouetteNote !== self.state.silhouette;
    });

    // Two completed-tower preview elements exist (Standard and Hex, each its
    // own GLB — see dice-tower-completed-preview.liquid), shown/hidden by
    // silhouette + pattern the same way per-part cards are below, rather
    // than swapping one <model-viewer>'s src (product-model's loadContent
    // doesn't support re-pointing an already-loaded element at a new file).
    this.root.querySelectorAll('[data-completed-tower-preview]').forEach(function (towerPreview) {
      var showTower = towerPreview.dataset.partSilhouette === self.state.silhouette
        && towerPreview.dataset.towerPattern === patternGroup;
      towerPreview.hidden = !showTower;
      if (!showTower) return;

      var towerModel = towerPreview.querySelector('product-model');
      if (towerModel && !towerModel.getAttribute('loaded')) {
        towerModel.loadContent(false);
        var previewSilhouette = towerPreview.dataset.partSilhouette;
        var waitForTowerViewer = function () {
          var mv = towerModel.querySelector('model-viewer');
          if (!mv) { requestAnimationFrame(waitForTowerViewer); return; }
          // See the matching comment in dice-tower-shop.js's loadAndWait —
          // model-viewer's own `model-visibility` event fires at its real
          // internal visibility transitions (a different threshold than
          // our own hidden-attribute-driven refreshVisibleParts calls),
          // which is precisely when its pause/resume logic can trigger the
          // reset recommitCameraOrbit/recommitFraming exist to correct.
          mv.addEventListener('model-visibility', function (e) {
            if (e.detail && e.detail.visible) self.recommitFraming(mv);
          });
          if (mv.model) { self.frameCompletedTower(mv, previewSilhouette); self.applyColorToCompletedTower(mv); return; }
          mv.addEventListener('load', function () {
            self.frameCompletedTower(mv, previewSilhouette);
            self.applyColorToCompletedTower(mv);
          }, { once: true });
        };
        waitForTowerViewer();
      } else {
        var existingTowerMv = towerPreview.querySelector('model-viewer');
        if (existingTowerMv) {
          self.applyColorToCompletedTower(existingTowerMv);
          self.recommitFraming(existingTowerMv);
        }
      }
    });

    // Lives in the panel (not the part grid) since it's a build-wide setting
    // rather than a per-card control — shown for any non-Standard Tower
    // pattern, not just Hex, so it keeps working as more patterns ship
    // their own Left/Right insert pairs.
    this.root.querySelectorAll('[data-link-toggle-wrap]').forEach(function (wrap) {
      wrap.hidden = !(self.state.silhouette === 'tower' && self.state.pattern !== 'standard');
    });

    this.root.querySelectorAll('.dice-configurator__part-card').forEach(function (card) {
      var matches = card.dataset.partSilhouette === self.state.silhouette && card.dataset.partPattern === patternGroup;
      card.hidden = !matches;
      if (!matches) return;

      var role = card.dataset.partRole;
      var isMulticolor = card.dataset.multicolor === 'true';
      var applyFn = isMulticolor
        ? function (mv) { self.applyZoneColorsToViewer(mv, role); }
        : function (mv) { self.applyColorToViewer(mv, role); };

      var productModel = card.querySelector('product-model');
      if (!productModel) return;
      if (!productModel.getAttribute('loaded')) {
        productModel.loadContent(false);
        // Recolor once the underlying <model-viewer> has actually parsed
        // the glTF (materials aren't available before its 'load' event).
        var waitForViewer = function () {
          var mv = productModel.querySelector('model-viewer');
          if (!mv) { requestAnimationFrame(waitForViewer); return; }
          mv.addEventListener('model-visibility', function (e) {
            if (e.detail && e.detail.visible) self.recommitFraming(mv);
          });
          // Only frame once the model itself is actually loaded — "auto"
          // zoom/distance is computed from the model's real bounding box,
          // so calling this before 'load' fires framed against no data.
          if (mv.model) { self.frameViewer(mv, role); applyFn(mv); return; }
          mv.addEventListener('load', function () {
            self.frameViewer(mv, role);
            applyFn(mv);
          }, { once: true });
        };
        waitForViewer();
      } else {
        var existingMv = card.querySelector('model-viewer');
        if (existingMv) {
          applyFn(existingMv);
          self.recommitFraming(existingMv);
        }
      }
    });
  };

  // No roles currently need a flipped default angle: the reconstructed Hex
  // insert GLBs (front-bottom-insert, front-top-insert, left-insert,
  // right-insert, lid-insert) were verified via screenshot at the plain
  // default 35deg/65deg angle already showing their decorative hex-pattern
  // face correctly — flipping to 215deg instead shows their flat backside.
  var FLIPPED_THETA_ROLES = {};

  // The reconstructed front-top-multicolor.glb comes straight from raw 3MF
  // mesh coordinates, where the panel's face normal lands on the model's
  // own Z axis instead of the Y axis glTF/model-viewer treats as "up" (the
  // real production panels were authored Y-up already, which is why the
  // generic angle below works for them but shows this one edge-on).
  // theta 0/phi 90 points the camera straight down that Z axis at the
  // face; nudged off both axes slightly for the same 3/4 look as every
  // other card instead of a flat frontal shot.
  var CAMERA_OVERRIDES = {
    'front-top': { theta: 20, phi: 80 },
    // insert-side-multicolor.glb's face normal lands on the model's X axis
    // rather than Z (its source 3MF is a tower SIDE panel, a different
    // natural orientation than the front panels above) — verified via
    // screenshot that the default angle showed its flat backside instead.
    'left-insert': { theta: -90, phi: 65 },
    'right-insert': { theta: -90, phi: 65 },
    // insert-vault-lid-multicolor.glb is a long, thin strip whose face
    // normal lands on the already-vertical Y axis — needs a near-top-down
    // angle instead of the generic 3/4 view. model-viewer's own default
    // min-camera-orbit floors phi at 22.5deg (to avoid gimbal-lock-like
    // views near the poles), silently clamping our declared 10deg up to
    // that floor — confirmed via getCameraOrbit() reading 22.5 even on a
    // first, never-switched-tabs visit. minPhi lowers that floor so 10deg
    // is actually reachable.
    'lid-insert': { theta: 0, phi: 10, minPhi: 0 },
    // Kumiko's reconstructed insert GLBs share Hex's own raw-3MF axis
    // convention exactly (verified via render screenshot this session —
    // Kumiko's source files happen to use the same thickness-axis-per-role
    // layout Hex's do), so its side/lid inserts need the same overrides;
    // its front-bottom/front-top inserts don't, matching Hex's own lack of
    // an override for those two roles.
    'left-insert-kumiko': { theta: -90, phi: 65 },
    'right-insert-kumiko': { theta: -90, phi: 65 },
    'lid-insert-kumiko': { theta: 0, phi: 10, minPhi: 0 },
    // Inverse Weave and Regal's source files use a DIFFERENT thickness-axis
    // convention than Hex/Kumiko's for front-bottom/front-top/side (their
    // raw 3MF thin axis is Z, not Y/X) — verified via render screenshot
    // that the plain default 35deg/65deg angle already shows their
    // decorative face correctly for all of front-bottom/front-top/side, so
    // unlike Hex/Kumiko's left/right-insert, these need NO override. Only
    // lid-insert needs one, same as every other pattern.
    'lid-insert-inverse-weave': { theta: 0, phi: 10, minPhi: 0 },
    'lid-insert-regal': { theta: 0, phi: 10, minPhi: 0 },
    // Wicker/Wyrmcron measured this session via true ground-truth three.js
    // rendering (render-glb.html + shoot-glb.js, recreated this session
    // after the prior copy was lost -- see node-tool/render-glb.html):
    // their side-insert canon lands the decorated face on the SAME
    // absolute convention as Hex/Kumiko's raw side-panel coords (not
    // Inverse Weave/Regal's), so it needs the same -90deg override --
    // confirmed visually (default 35/65 showed the blank back, -90/65
    // showed the woven/etched decorative front). front-bottom/front-top
    // need no override, matching Inverse Weave/Regal's precedent exactly
    // (also confirmed visually). lid-insert needs the standard strip
    // override, same as every other pattern.
    'left-insert-wicker': { theta: -90, phi: 65 },
    'right-insert-wicker': { theta: -90, phi: 65 },
    'lid-insert-wicker': { theta: 0, phi: 10, minPhi: 0 },
    'left-insert-wyrmcron': { theta: -90, phi: 65 },
    'right-insert-wyrmcron': { theta: -90, phi: 65 },
    'lid-insert-wyrmcron': { theta: 0, phi: 10, minPhi: 0 },
    // Spiral: same -90deg side-insert override as Wicker/Wyrmcron, confirmed
    // this session via the same shoot-glb.js debug-colored render check
    // (default 35/65 would show the blank back; -90/65 shows the spiral
    // groove pattern) plus the Kabsch solve's own R≈IDENT/MIRROR_Z result,
    // which independently confirms the canon already lands the decorated
    // face on this same absolute convention.
    'left-insert-spiral': { theta: -90, phi: 65 },
    'right-insert-spiral': { theta: -90, phi: 65 },
    'lid-insert-spiral': { theta: 0, phi: 10, minPhi: 0 },
    // The real "Dice Tower - .../Left/Right/Back Panel" product GLBs (both
    // Standard and Patterned share this orientation) show as a thin
    // diagonal sliver at the generic angle — verified via screenshot that
    // theta 90 shows the actual full flat panel face instead. Left is
    // theta -90 rather than 90 (the mirror of Right) since 90 showed the
    // panel's inside face — -90 is the outside, the side that actually
    // shows once the tower is assembled.
    left: { theta: -90, phi: 65 },
    right: { theta: 90, phi: 65 },
    back: { theta: 0, phi: 65 }
  };

  // model-viewer pauses its internal render loop while its element is not
  // visible (confirmed via direct testing for both `hidden`/display:none —
  // as happens whenever a silhouette or pattern tab switches away — and
  // scrolled fully off-screen) — and coming back can leave its LIVE camera
  // silently reset to the library's own hardcoded default (0deg/75deg)
  // while the declared camera-orbit ATTRIBUTE is untouched and still reads
  // correctly. Confirmed empirically (a closed-source model-viewer
  // internal timing quirk, not something reachable from our own code):
  // re-writing the SAME already-correct value plus jumpCameraToGoal()
  // doesn't reliably recover it on the very next frame either — during
  // fast continuous scrolling the reset can recur several times as the
  // element's real on/off-screen state flickers faster than a single
  // follow-up catches. Re-asserting for a short run of frames (~200ms)
  // instead of just one closes that race. Cheap and side-effect-free
  // either way: it never changes the declared value, only re-commits it,
  // so calling it far more than necessary is harmless.
  function recommitCameraOrbit(mv) {
    if (!mv || typeof mv.jumpCameraToGoal !== 'function') return;
    var orbit = mv.getAttribute('camera-orbit');
    var framesLeft = 12; // ~200ms of safety margin at 60fps
    function reassert() {
      if (orbit) mv.cameraOrbit = orbit;
      mv.jumpCameraToGoal();
      framesLeft--;
      if (framesLeft > 0) requestAnimationFrame(reassert);
    }
    reassert();
  }

  // F7.1 fix: model-viewer's own built-in animated "drag to rotate" hint
  // was unconditionally suppressed on every viewer on the site
  // (interaction-prompt="none"), with no theme-authored replacement
  // anywhere — a first-time visitor had no way to discover these are
  // interactive 3D models rather than static renders. Showing it once per
  // browser (the first model that loads, tracked via localStorage) teaches
  // the interaction without a wall of ~13 simultaneous hand-drag
  // animations across every card a pattern can show at once.
  var ROTATE_HINT_STORAGE_KEY = 'diceTowerRotateHintShown';
  var rotateHintDecided = false;
  function shouldShowRotateHint() {
    if (rotateHintDecided) return false;
    rotateHintDecided = true;
    try {
      if (window.localStorage.getItem(ROTATE_HINT_STORAGE_KEY)) return false;
      window.localStorage.setItem(ROTATE_HINT_STORAGE_KEY, '1');
      return true;
    } catch (e) {
      // Storage unavailable (private browsing, cookies disabled, etc.) —
      // default to not showing the hint rather than risk it firing on
      // every card every time due to a storage failure.
      return false;
    }
  }

  // A consistent 3/4 angle for every part, with the camera distance set to
  // "auto" so the whole part is always framed in view regardless of its
  // individual proportions — each model's own default orbit otherwise
  // varies (and for a mostly-flat printed panel can end up nearly edge-on).
  DiceTowerConfigurator.prototype.frameViewer = function (mv, role) {
    if (!mv || mv.dataset.framed) return;
    mv.dataset.framed = 'true';
    var override = CAMERA_OVERRIDES[role];
    var theta = override ? override.theta : (FLIPPED_THETA_ROLES[role] ? 215 : 35);
    var phi = override ? override.phi : 65;
    mv.setAttribute('camera-orbit', theta + 'deg ' + phi + 'deg auto');
    mv.setAttribute('interaction-prompt', shouldShowRotateHint() ? 'auto' : 'none');
    mv.setAttribute('field-of-view', 'auto');
    if (override && typeof override.minPhi === 'number') {
      mv.setAttribute('min-camera-orbit', 'auto ' + override.minPhi + 'deg auto');
    }
    recommitCameraOrbit(mv);
  };

  // Per-silhouette viewing angle for the completed previews. Tower stays
  // level (phi=90) — its own top opening isn't a feature worth revealing
  // and looking up into its open bottom would expose the hollow interior.
  // Vault and tray get a lower phi (tilted down from above) specifically
  // so their top surfaces — the vault's patterned lid, the tray's inner
  // rolling surface and accent bands — are actually visible, per explicit
  // request ("rotate the dice vault/tray down so the top can be seen").
  // targetXFrac/targetZFrac nudge the look-at point sideways as a
  // fraction of the model's own half-width, in local (pre-rotation) X/Z —
  // used to shift the tray right so its clamp and every color zone clear
  // the card's edges once the steeper angle changes what's near-clipped.
  var COMPLETED_CAMERA = {
    tower: { theta: 45, phi: 90, margin: 1.28, targetXFrac: 0, targetZFrac: 0 },
    vault: { theta: 45, phi: 65, margin: 1.3, targetXFrac: 0, targetZFrac: 0 },
    tray: { theta: 25, phi: 60, margin: 1.35, targetXFrac: 0.12, targetZFrac: 0 }
  };

  // Every completed-* GLB (tower, vault, tray) is exported Y-up with the
  // assembly centered on X/Z and floored at Y=0 (see each export
  // pipeline's own notes).
  //
  // All three completed-preview cards share the tower's own portrait
  // aspect box (padding-top: 150%), per explicit request that every
  // preview window be the same size. model-viewer's own "auto" radius
  // fits the model's bounding SPHERE, which is a poor match for a long,
  // flat model like the vault or tray inside a TALL box: a sphere big
  // enough to contain a 202mm-long, 30mm-tall bar is far larger than that
  // bar's own on-screen footprint from this angle, so sphere-fit left the
  // vault/tray tiny with huge empty space above and below. Fitting the
  // model's ACTUAL silhouette at this viewing angle instead — using
  // model-viewer's own getDimensions() (true AABB size) — fills the frame
  // correctly regardless of how each model's proportions differ. This is
  // still an approximation once phi departs from 90deg (tilting down
  // trades some height foreshortening for revealed depth, which a pure
  // AABB-diagonal estimate doesn't fully capture), so vault/tray carry a
  // larger margin above to cover that slack rather than risk clipping.
  DiceTowerConfigurator.prototype.frameCompletedTower = function (mv, silhouette) {
    if (!mv || mv.dataset.framed) return;
    mv.dataset.framed = 'true';
    var override = COMPLETED_CAMERA[silhouette] || COMPLETED_CAMERA.tower;
    var theta = override.theta, phi = override.phi;
    mv.setAttribute('interaction-prompt', shouldShowRotateHint() ? 'auto' : 'none');
    mv.setAttribute('field-of-view', 'auto');
    mv.setAttribute('camera-orbit', theta + 'deg ' + phi + 'deg auto');
    if (typeof mv.jumpCameraToGoal === 'function') mv.jumpCameraToGoal();

    var hasFitApi = typeof mv.getCameraTarget === 'function'
      && typeof mv.getDimensions === 'function'
      && typeof mv.getFieldOfView === 'function';
    if (!hasFitApi) return;

    var target = mv.getCameraTarget();
    var dims = mv.getDimensions();
    var halfWidth = (dims.x + dims.z) * Math.SQRT1_2 / 2;
    var halfHeight = dims.y / 2;
    var vFov = mv.getFieldOfView() * Math.PI / 180;
    var rect = mv.getBoundingClientRect();
    var aspect = rect && rect.height ? rect.width / rect.height : (1 / 1.5);
    var hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
    // Whichever dimension is the tighter constraint (usually height for
    // the tower, width for the vault/tray) sets the distance — using the
    // other would let that dimension overflow the card.
    var fitDistance = Math.max(halfHeight / Math.tan(vFov / 2), halfWidth / Math.tan(hFov / 2));
    var margin = override.margin;
    var radius = (fitDistance * margin).toFixed(4);
    var shiftedTarget = (target.x + halfWidth * (override.targetXFrac || 0)).toFixed(2) + 'm '
      + target.y.toFixed(2) + 'm '
      + (target.z + halfWidth * (override.targetZFrac || 0)).toFixed(2) + 'm';
    mv.setAttribute('camera-target', shiftedTarget);
    mv.setAttribute('max-camera-orbit', 'auto auto ' + radius + 'm');
    mv.setAttribute('camera-orbit', theta + 'deg ' + phi + 'deg ' + radius + 'm');
    recommitCameraOrbit(mv);
  };

  // Re-commits the ALREADY-correct camera/target for a card that was
  // framed earlier and is simply becoming visible again (a silhouette or
  // pattern tab switch) — never recomputes anything, just guards against
  // the same off-screen reset recommitCameraOrbit documents above. Safe to
  // call on every revisit, including completed-tower previews (whose
  // camera-target/max-camera-orbit are already baked into attributes from
  // frameCompletedTower's one-time computation and must never be
  // recalculated a second time, only re-asserted).
  DiceTowerConfigurator.prototype.recommitFraming = function (mv) {
    recommitCameraOrbit(mv);
  };

  // Recolors the assembled completed-tower preview from the exact same
  // partColors/zoneColors state the per-part cards already read from — no
  // separate color-state system, so the two views can't drift apart. Each
  // mesh in completed-tower*.glb was exported with its material named after
  // the matching configurator role (see the 3MF->GLB export notes), so
  // materials are matched by name rather than by array position. Hex-only
  // zoned parts (the 4 insert pieces) follow a "<role>-<zoneIndex>" naming
  // convention instead, since one completed-tower-hex.glb material can't
  // carry a whole zone array — the regex below splits that back apart.
  DiceTowerConfigurator.prototype.applyColorToCompletedTower = function (mv) {
    if (!mv || !mv.model) return;
    var self = this;
    try {
      mv.model.materials.forEach(function (material) {
        var name = material.name;
        if (name === 'front-top-body' || name === 'front-top-logo') {
          var zones = self.state.zoneColors['front-top'];
          if (!zones) return;
          var zoneColor = name === 'front-top-body' ? zones[0] : zones[1];
          if (zoneColor) self.applyFlatColor(material, zoneColor.hex);
          return;
        }
        var zoneMatch = /^(.+)-(\d+)$/.exec(name);
        if (zoneMatch && MULTICOLOR_ZONES[zoneMatch[1]]) {
          var zRole = zoneMatch[1];
          // The number baked into the material's own name (from
          // build_pattern.py) is the GLB's true, fixed materialIndex —
          // NOT necessarily this role's zoneColors array position anymore
          // (Spiral's color-picker display order deliberately differs
          // from materialIndex order — see MULTICOLOR_ZONES' own comment
          // on Spiral). Map back to whichever array position actually
          // carries that materialIndex before reading zColors.
          var zMaterialIndex = parseInt(zoneMatch[2], 10);
          var zColors = self.state.zoneColors[zRole];
          var zDefs = MULTICOLOR_ZONES[zRole];
          var zDisplayIndex = zMaterialIndex;
          if (zDefs) {
            for (var zi = 0; zi < zDefs.length; zi++) {
              if (zDefs[zi].materialIndex === zMaterialIndex) { zDisplayIndex = zi; break; }
            }
          }
          if (zColors && zColors[zDisplayIndex]) self.applyFlatColor(material, zColors[zDisplayIndex].hex);
          return;
        }
        var color = self.state.partColors[name];
        if (color) self.applyFlatColor(material, color.hex);
      });
    } catch (e) {
      // Model not fully ready yet — refreshVisibleParts()'s load listener
      // will retry once it is.
    }
  };

  DiceTowerConfigurator.prototype.refreshCompletedTowerColors = function () {
    var self = this;
    this.root.querySelectorAll('[data-completed-tower-preview]').forEach(function (towerPreview) {
      var mv = towerPreview.querySelector('model-viewer');
      if (mv) self.applyColorToCompletedTower(mv);
    });
    // Every caller of this method just changed some color state — this is
    // the one choke point every color-mutating action already runs
    // through, so it's also the simplest place to keep the F3.2 selections
    // summary in sync without adding a call at each individual swatch
    // handler.
    this.updateSelectionsSummary();
  };

  DiceTowerConfigurator.prototype.applyColorToVisibleParts = function () {
    var self = this;
    this.root.querySelectorAll('.dice-configurator__part-card:not([hidden])').forEach(function (card) {
      var mv = card.querySelector('model-viewer');
      if (!mv) return;
      var role = card.dataset.partRole;
      if (card.dataset.multicolor === 'true') {
        self.applyZoneColorsToViewer(mv, role);
      } else {
        self.applyColorToViewer(mv, role);
      }
    });
  };

  // glTF's PBR model MULTIPLIES baseColorFactor with any baseColorTexture
  // the material already has — so setting a factor alone doesn't replace
  // whatever's baked into the texture, it tints on top of it, producing a
  // muddied/mixed result instead of the pure selected color. Stripping the
  // texture first makes the factor the sole color source.
  DiceTowerConfigurator.prototype.applyFlatColor = function (material, hex) {
    try {
      if (material.pbrMetallicRoughness.baseColorTexture) {
        material.pbrMetallicRoughness.baseColorTexture.setTexture(null);
      }
    } catch (e) { /* no texture to strip — fine */ }
    material.pbrMetallicRoughness.setBaseColorFactor(this.hexToFactor(hex));
  };

  DiceTowerConfigurator.prototype.applyColorToViewer = function (mv, role) {
    if (!mv || !mv.model) return;
    var color = this.state.partColors[role];
    if (!color) return;
    var self = this;
    try {
      mv.model.materials.forEach(function (m) { self.applyFlatColor(m, color.hex); });
    } catch (e) {
      // Model not fully ready yet — refreshVisibleParts()'s load listener
      // will retry once it is.
    }
  };

  // For multicolor parts: recolors each real material independently by
  // index, instead of applying one color to every material like
  // applyColorToViewer does for single-material parts.
  // Updates just the swatch-row UI (which button shows as selected) for one
  // zone on one card, without touching state or the 3D viewer — used to
  // reflect a mirrored color change from the other side of a locked pair.
  DiceTowerConfigurator.prototype.setZoneSwatchUI = function (role, zoneIndex, hex) {
    var card = this.root.querySelector('.dice-configurator__part-card[data-part-role="' + role + '"]');
    if (!card) return;
    var row = card.querySelectorAll('.dice-configurator__zone-row')[zoneIndex];
    if (!row) return;
    row.querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
      b.classList.toggle('is-selected', b.dataset.zoneColorHex === hex);
    });
  };

  // Same idea as setZoneSwatchUI, for a single-color (non-multicolor)
  // part's own one swatch row instead of one zone-row among several.
  DiceTowerConfigurator.prototype.setPartSwatchUI = function (role, hex) {
    var card = this.root.querySelector('.dice-configurator__part-card[data-part-role="' + role + '"]');
    if (!card) return;
    card.querySelectorAll('.dice-configurator__part-swatch').forEach(function (b) {
      b.classList.toggle('is-selected', b.dataset.colorHexValue === hex);
    });
  };

  // F5.3: shows/hides the "Undo last color change" button based on
  // whether this.lastColorChange is currently set.
  DiceTowerConfigurator.prototype.updateUndoButtonVisibility = function () {
    var btn = this.root.querySelector('[data-undo-last-color]');
    if (btn) btn.hidden = !this.lastColorChange;
  };

  // F5.3: reverts the single most recent per-part or per-zone swatch
  // click (including any crest-emblem sync or Left/Right mirror it also
  // triggered) back to its previous color(s) — see the data-part-color-hex
  // and data-zone-color-hex handlers in bindEvents for where
  // this.lastColorChange gets set.
  DiceTowerConfigurator.prototype.undoLastColorChange = function () {
    var self = this;
    var change = this.lastColorChange;
    if (!change) return;

    if (change.kind === 'part') {
      change.partChanges.forEach(function (c) {
        self.state.partColors[c.role] = c.previousColor;
        self.setPartSwatchUI(c.role, c.previousColor.hex);
        var card = self.root.querySelector('.dice-configurator__part-card[data-part-role="' + c.role + '"]');
        var mv = card && card.querySelector('model-viewer');
        if (mv) self.applyColorToViewer(mv, c.role);
      });
    } else if (change.kind === 'zone') {
      change.zoneChanges.forEach(function (c) {
        self.state.zoneColors[c.role][c.zoneIndex] = c.previousColor;
        self.setZoneSwatchUI(c.role, c.zoneIndex, c.previousColor.hex);
        var card = self.root.querySelector('.dice-configurator__part-card[data-part-role="' + c.role + '"]');
        var mv = card && card.querySelector('model-viewer');
        if (mv) self.applyZoneColorsToViewer(mv, c.role);
      });
    }

    this.lastColorChange = null;
    this.updateUndoButtonVisibility();
    this.refreshCompletedTowerColors();
  };

  // Randomize All Colors — an independent random pick per role (partColors)
  // and per zone (zoneColors), unlike the "Set all" swatch which assigns
  // ONE color everywhere. Deliberately mirrors "Set all"'s own scope
  // (Object.keys(state.partColors)/(state.zoneColors), not just the
  // currently-visible silhouette/pattern) for the same reason "Set all"
  // already works that way — every role for every pattern the customer
  // might switch back to gets its own random color too, not just what's
  // on screen right now.
  DiceTowerConfigurator.prototype.randomizeAllColors = function () {
    var self = this;
    function randomColor() {
      return COLORS[Math.floor(Math.random() * COLORS.length)];
    }
    Object.keys(this.state.partColors).forEach(function (role) {
      var color = randomColor();
      self.state.partColors[role] = { hex: color.hex, name: color.name };
      self.setPartSwatchUI(role, color.hex);
    });
    Object.keys(this.state.zoneColors).forEach(function (role) {
      self.state.zoneColors[role] = self.state.zoneColors[role].map(function (_zone, i) {
        var color = randomColor();
        self.setZoneSwatchUI(role, i, color.hex);
        return { hex: color.hex, name: color.name };
      });
    });
    this.applyColorToVisibleParts();
    this.refreshCompletedTowerColors();
    // F5.3: same reasoning as the "Set a Single Color" confirm branch —
    // a full randomize makes a single-part undo no longer meaningful, and
    // this action already has its own click-twice-confirm safety net.
    this.lastColorChange = null;
    this.updateUndoButtonVisibility();
  };

  DiceTowerConfigurator.prototype.applyZoneColorsToViewer = function (mv, role) {
    if (!mv || !mv.model) return;
    var zones = this.state.zoneColors[role];
    var zoneDefs = MULTICOLOR_ZONES[role];
    if (!zones) return;
    var self = this;
    try {
      zones.forEach(function (zoneColor, i) {
        // materialIndex decouples the SWATCH ROW'S display order (this
        // array's own position, which the store owner wants free to
        // reorder — e.g. Spiral's Pattern row moved to display last) from
        // the GLB's actual, fixed material order — see the doc comment on
        // MULTICOLOR_ZONES's Spiral entries for the concrete example.
        // Falls back to plain position for any role that doesn't declare
        // materialIndex, though every current entry does.
        var materialIndex = (zoneDefs && zoneDefs[i] && zoneDefs[i].materialIndex !== undefined) ? zoneDefs[i].materialIndex : i;
        var material = mv.model.materials[materialIndex];
        if (material) self.applyFlatColor(material, zoneColor.hex);
      });
    } catch (e) {
      // Model not fully ready yet — refreshVisibleParts()'s load listener
      // will retry once it is.
    }
  };

  // glTF's baseColorFactor is defined in LINEAR color space, but our swatch
  // hex values (like every color on the web) are sRGB-encoded — feeding
  // sRGB values in directly without decoding renders every color lighter/
  // washed out than intended, since the renderer re-applies its own
  // linear-to-sRGB encoding on display on top of values that were never
  // linear to begin with.
  function srgbToLinear(c) {
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  DiceTowerConfigurator.prototype.hexToFactor = function (hex) {
    var m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return [1, 1, 1, 1];
    return [
      srgbToLinear(parseInt(m[1], 16) / 255),
      srgbToLinear(parseInt(m[2], 16) / 255),
      srgbToLinear(parseInt(m[3], 16) / 255),
      1
    ];
  };

  DiceTowerConfigurator.prototype.updatePrice = function () {
    var isPatterned = this.state.pattern !== 'standard';
    var tier = isPatterned ? 'patterned' : 'standard';
    var total = PRICES.tower[tier] + PRICES.vault[tier] + PRICES.tray.standard + PRICES.clamp.standard;
    var priceEl = this.root.querySelector('[data-price-total]');
    if (priceEl) priceEl.textContent = '$' + total.toFixed(0);

    // F6.2 fix: the total was a single lump number with no way to see
    // what each component actually costs. Reads straight from the same
    // PRICES object the total above is computed from, so this can't drift
    // out of sync with it. 2 decimal places here (unlike the whole-dollar
    // total) since the individual prices carry cents that only sum to a
    // whole dollar together — rounding each line first could make them
    // visibly fail to add up to the total shown above.
    var breakdownList = this.root.querySelector('[data-price-breakdown-list]');
    if (breakdownList) {
      breakdownList.innerHTML = '';
      [
        ['Tower', PRICES.tower[tier]],
        ['Vault', PRICES.vault[tier]],
        ['Tray', PRICES.tray.standard],
        ['Clamp', PRICES.clamp.standard]
      ].forEach(function (row) {
        var dt = document.createElement('dt');
        dt.textContent = row[0];
        var dd = document.createElement('dd');
        dd.textContent = '$' + row[1].toFixed(2);
        breakdownList.appendChild(dt);
        breakdownList.appendChild(dd);
      });
    }
  };

  // Builds a readable "Part: Color, Part: Color, ..." string for one
  // silhouette's line item — collapses to a single "All parts: Color" when
  // every part in that component happens to share the same color, since
  // that's the common case and reads much cleaner on the order.
  DiceTowerConfigurator.prototype.colorSummaryFor = function (silhouette) {
    var self = this;
    var patternGroup = patternGroupFor(this.state.pattern);
    var roles = SILHOUETTE_ROLES[silhouette][patternGroup] || SILHOUETTE_ROLES[silhouette].standard;

    // Multicolor parts always list their own zone breakdown in detail and
    // never participate in the "every part matches, collapse to one line"
    // check below — a multi-material part inherently isn't a single color.
    var singleColorRoles = roles.filter(function (role) { return !MULTICOLOR_ZONES[role]; });
    var multicolorRoles = roles.filter(function (role) { return MULTICOLOR_ZONES[role]; });

    var names = singleColorRoles.map(function (role) { return self.state.partColors[role].name; });
    var allSame = names.length > 0 && multicolorRoles.length === 0 && names.every(function (n) { return n === names[0]; });

    var parts = allSame
      ? ['All parts: ' + names[0]]
      : singleColorRoles.map(function (role) { return ROLE_LABELS[role] + ': ' + self.state.partColors[role].name; });

    multicolorRoles.forEach(function (role) {
      var zoneText = MULTICOLOR_ZONES[role].map(function (zone, i) {
        return zone.label + ' ' + self.state.zoneColors[role][i].name;
      }).join('/');
      parts.push(ROLE_LABELS[role] + ' (' + zoneText + ')');
    });

    return parts.join(', ');
  };

  var SELECTIONS_SUMMARY_LABELS = { tower: 'Tower', vault: 'Vault', tray: 'Tray', clamp: 'Clamp' };

  // F3.2 fix: renders the same per-component text colorSummaryFor() above
  // already builds for the cart line-item properties into a visible
  // "Your Selections" panel, so a customer can check their whole build
  // without scrolling back through every part card to recall what they
  // set. Called from every place that already recolors the completed-tower
  // previews (i.e. every color-mutating action) plus the pattern-switch
  // handler, since colorSummaryFor's role list itself depends on the
  // active pattern.
  DiceTowerConfigurator.prototype.updateSelectionsSummary = function () {
    var self = this;
    var list = this.root.querySelector('[data-selections-summary]');
    if (!list) return;
    list.innerHTML = '';
    ['tower', 'vault', 'tray', 'clamp'].forEach(function (silhouette) {
      var dt = document.createElement('dt');
      dt.textContent = SELECTIONS_SUMMARY_LABELS[silhouette];
      var dd = document.createElement('dd');
      dd.textContent = self.colorSummaryFor(silhouette);
      list.appendChild(dt);
      list.appendChild(dd);
    });
  };

  // Produces a flat {materialName: hex} map for every material that
  // actually exists on the ONE shared GLB asset the cart 3D preview uses
  // for this silhouette (completed-tower-<pattern>.glb / completed-vault-
  // <pattern>.glb / the shared completed-tray.glb for both Tray and
  // Clamp) — material naming here MUST exactly match what those GLBs
  // actually contain (verified via build_pattern.py/build_completed_tower
  // and inspect_bbox.py this session): multicolor zone roles become
  // '<role>-<index>' (e.g. 'front-bottom-insert-spiral-2'), Standard's
  // front-top is the one special case with genuinely different material
  // names ('front-top-body'/'front-top-logo', not the generic zone
  // pattern — matches applyColorToCompletedTower's own special-case for
  // the same reason), and every other role maps straight to
  // state.partColors[role].hex under its own bare name. This is only ever
  // used to SERIALIZE color state onto the cart line item's hidden
  // properties at add-to-cart time (see buildLineItems below) — the cart
  // page has no access to this JS file or this.state at all, so this is
  // the one chance to hand it everything it'll need to reconstruct the
  // exact same colors later via assets/cart-3d-preview.js.
  DiceTowerConfigurator.prototype.buildMaterialColorMapFor = function (silhouette) {
    var self = this;
    var patternGroup = patternGroupFor(this.state.pattern);
    var roles = SILHOUETTE_ROLES[silhouette][patternGroup] || SILHOUETTE_ROLES[silhouette].standard;
    var map = {};
    roles.forEach(function (role) {
      if (role === 'front-top' && self.state.zoneColors['front-top']) {
        map['front-top-body'] = self.state.zoneColors['front-top'][0].hex;
        map['front-top-logo'] = self.state.zoneColors['front-top'][1].hex;
      } else if (MULTICOLOR_ZONES[role]) {
        // Cart material names are baked into the GLB in true material-index
        // order (build_pattern.py), not display order — see the matching
        // comment in applyZoneColorsToViewer for why materialIndex, not
        // array position i, is the right thing to key by here too.
        self.state.zoneColors[role].forEach(function (c, i) {
          var materialIndex = (MULTICOLOR_ZONES[role][i] && MULTICOLOR_ZONES[role][i].materialIndex !== undefined) ? MULTICOLOR_ZONES[role][i].materialIndex : i;
          map[role + '-' + materialIndex] = c.hex;
        });
      } else {
        map[role] = self.state.partColors[role].hex;
      }
    });
    return map;
  };

  // `Build` is a normal (non-underscore) property, so unlike `_build_id`
  // it's NOT hidden by Shopify on the cart page, checkout, order status
  // page, or order confirmation email — it's the one piece of this data
  // actually meant for people to read. `_build_id` stays alongside it as
  // an exact, unambiguous machine key (still visible to store staff in
  // the admin order view, just not to the customer) in case two builds
  // ever end up with identical-looking Color/Pattern text and a human
  // skimming "Build #2" needs a tiebreaker. `_pattern_key` and
  // `_material_colors` are the same kind of hidden machine data, added so
  // the cart page's 3D preview (assets/cart-3d-preview.js) can pick the
  // right completed-tower/vault GLB and recolor it exactly, without having
  // to re-parse the human-readable `Color` summary string.
  DiceTowerConfigurator.prototype.buildLineItems = function (buildNumber) {
    var isPatterned = this.state.pattern !== 'standard';
    var tier = isPatterned ? 'patterned' : 'standard';
    var patternLabel = PATTERNS.filter(function (p) { return p.key === this.state.pattern; }, this)[0].label;
    var buildId = 'build_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    var buildLabel = 'Build #' + buildNumber;

    return [
      {
        id: VARIANTS.tower[tier],
        quantity: 1,
        properties: {
          Build: buildLabel,
          Component: 'Tower',
          Color: this.colorSummaryFor('tower'),
          Pattern: patternLabel,
          _build_id: buildId,
          _pattern_key: this.state.pattern,
          _material_colors: JSON.stringify(this.buildMaterialColorMapFor('tower'))
        }
      },
      {
        id: VARIANTS.vault[tier],
        quantity: 1,
        properties: {
          Build: buildLabel,
          Component: 'Vault',
          Color: this.colorSummaryFor('vault'),
          Pattern: patternLabel,
          _build_id: buildId,
          _pattern_key: this.state.pattern,
          _material_colors: JSON.stringify(this.buildMaterialColorMapFor('vault'))
        }
      },
      {
        id: VARIANTS.tray.standard,
        quantity: 1,
        properties: {
          Build: buildLabel,
          Component: 'Tray',
          Color: this.colorSummaryFor('tray'),
          _build_id: buildId,
          _material_colors: JSON.stringify(this.buildMaterialColorMapFor('tray'))
        }
      },
      {
        id: VARIANTS.clamp.standard,
        quantity: 1,
        properties: {
          Build: buildLabel,
          Component: 'Clamp',
          Color: this.colorSummaryFor('clamp'),
          _build_id: buildId,
          _material_colors: JSON.stringify(this.buildMaterialColorMapFor('clamp'))
        }
      }
    ];
  };

  // F1.3/F4.1 fix: this used to hard-navigate to /cart on success, the only
  // one of the site's 3 add-to-cart flows that left the page — every other
  // add-to-cart (a normal product page, the individual-parts shop) opens
  // the cart drawer/notification in place instead. The drawer already runs
  // the same recolored 3D build preview as the full cart page (see
  // assets/cart-3d-preview.js's own header comment — it explicitly targets
  // both surfaces), so a redirect isn't needed for that preview to show;
  // switching to the same renderContents() pattern product-form.js and
  // dice-tower-shop.js use makes all 3 flows behave the same way.
  var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

  DiceTowerConfigurator.prototype.addToCart = function () {
    var self = this;
    var btn = this.root.querySelector('[data-add-to-cart]');
    var statusEl = this.root.querySelector('[data-add-to-cart-status]');
    btn.disabled = true;
    if (statusEl) statusEl.textContent = 'Adding to cart…';

    var routeRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

    // Count builds already sitting in the cart (by distinct _build_id, not
    // by line count — a build can be 3 or 4 items depending on Clamp) so a
    // customer adding a second or third build in the same order gets
    // "Build #2" / "Build #3" instead of every build confusingly reading
    // "Build #1".
    fetch(routeRoot + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var existingBuildIds = {};
        (cart.items || []).forEach(function (item) {
          var bid = item.properties && item.properties._build_id;
          if (bid) existingBuildIds[bid] = true;
        });
        var buildNumber = Object.keys(existingBuildIds).length + 1;

        var payload = { items: self.buildLineItems(buildNumber) };
        if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
          payload.sections = cartEl.getSectionsToRender().map(function (section) { return section.id; });
          payload.sections_url = window.location.pathname;
        }

        return fetch(routeRoot + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
      })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw new Error(err.description || 'Add to cart failed'); });
        return res.json();
      })
      .then(function (response) {
        btn.disabled = false;
        if (statusEl) statusEl.textContent = 'Added to cart!';
        if (cartEl && typeof cartEl.renderContents === 'function') {
          if (cartEl.setActiveElement) cartEl.setActiveElement(btn);
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dice-tower-configurator', cartData: response });
          }
          // Bug fix: this adds multiple line items (Tower/Vault/Tray/
          // Clamp) in ONE request by design (shared _build_id) — Shopify's
          // /cart/add.js response for a multi-item POST is `{items: [...]}`,
          // with no top-level `.key` the way a single-item add response
          // has. CartNotification.renderContents() reads `parsedState.key`
          // to pick which "recently added product" card to show, and its
          // cart-notification-product section lookup THROWS when that's
          // undefined — silently aborting the rest of its render loop
          // before it ever reached the cart-icon-bubble section later in
          // that same list, which is why the header count stayed stale
          // until a full reload. CartDrawer doesn't depend on `.key` at
          // all, so this shim is a harmless no-op there — it only fills in
          // what CartNotification specifically needs.
          var renderPayload = response;
          if (!renderPayload.key && renderPayload.items && renderPayload.items.length) {
            renderPayload = Object.assign({}, response, { key: response.items[0].key });
          }
          cartEl.renderContents(renderPayload);
        } else {
          window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart_url) || '/cart';
        }
      })
      .catch(function (err) {
        btn.disabled = false;
        if (statusEl) statusEl.textContent = err.message || 'Something went wrong — please try again.';
      });
  };

  function init() {
    document.querySelectorAll('[data-dice-tower-configurator]').forEach(function (root) {
      new DiceTowerConfigurator(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
